import { createEvent, createStore } from 'effector';
import { effectorStartEvent, sortByIndex } from '@nx-frontend/shared/store/client-side';
import {
  BlockActionsData,
  BlockDataData,
  BlockIndexData,
  BlockPlaceData,
  BlockSizeData,
  BlockUpdateData,
  BlockVisibilityData,
  ContentBlock,
  createInitialEntityState,
  EntityState,
  FlexibleBlockTemplateType,
  PortalBlock,
  PortalBlockPlace,
  PortalBlockType,
} from '@nx-frontend/shared/types';
import { arrayMove } from '@nx-frontend/shared/utils';

const blockPlaces: PortalBlockPlace[] = [
  'global_header',
  'global_body',
  'global_footer',
  'page_header',
  'page_body_top',
  'page_body',
  'page_body_bottom',
  'page_footer',
];

type BlocksState = {
  [key in PortalBlockPlace]: EntityState<PortalBlock>;
};
type BlocksByPlace = {
  [key in PortalBlockPlace]: PortalBlock[];
};
type BlocksOrderByPlace = {
  [key in PortalBlockPlace]: string[];
};
type HasNoteBlocksByPlace = {
  [key in PortalBlockPlace]: boolean;
};

const createInitialBlocksState = (): BlocksState => {
  return blockPlaces.reduce((acc, place) => {
    acc[place] = createInitialEntityState<PortalBlock>();
    return acc;
  }, {} as BlocksState);
};

export const setBlocksEvent = createEvent<PortalBlock[]>();
export const addBlockEvent = createEvent<PortalBlock>();
export const deleteBlockEvent = createEvent<PortalBlock>();
export const updateBlockEvent = createEvent<BlockUpdateData>();
export const updateBlockSizeEvent = createEvent<BlockSizeData>();
export const updateBlockVisibilityEvent = createEvent<BlockVisibilityData>();
export const pushBlockDataEvent = createEvent<BlockDataData>();
export const updateBlockPlaceEvent = createEvent<BlockPlaceData>();
export const updateBlockIndexEvent = createEvent<BlockIndexData>();
export const updateBlockActionsEvent = createEvent<BlockActionsData>();
export const setBlocksLoadingEvent = createEvent<boolean>();

export const blocksToState = (blocks: PortalBlock[]): BlocksState => {
  const newState = blocks.reduce((acc, block) => {
    acc[block.place].entities[block.id] = block;
    if (!acc[block.place].ids.includes(block.id)) {
      acc[block.place].ids.push(block.id);
    }
    return acc;
  }, createInitialBlocksState());
  for (const key in newState) {
    const place = key as PortalBlockPlace;
    newState[place as PortalBlockPlace].ids = sortByIndex(newState[place].ids, newState[place].entities);
  }
  return newState;
};

export const stateToBlocks = (state: BlocksState): PortalBlock[] => {
  return Object.values(state).reduce<PortalBlock[]>((acc, { entities }) => {
    acc.push(...Object.values(entities));
    return acc;
  }, []);
};

export const updateBlockInArray = (
  blocks: PortalBlock[],
  update: BlockUpdateData | BlockSizeData | BlockPlaceData | BlockIndexData | BlockActionsData | BlockVisibilityData
): PortalBlock[] => {
  const index = blocks.findIndex(({ id }) => id === update.id);
  if (index !== -1) {
    const oldBlock = { ...blocks[index] };
    blocks[index] = { ...oldBlock, ...update, external: oldBlock.external, pageId: oldBlock.pageId };
  }
  return [...blocks];
};

export interface BlocksStore {
  loading: boolean;
  blocks: PortalBlock[];
}

export const initialBlocksStore: BlocksStore = {
  loading: true,
  blocks: [],
};

export const $blocksStore = createStore(initialBlocksStore)
  .on(effectorStartEvent, (store) => store)
  .on(setBlocksLoadingEvent, (store, loading) => ({ ...store, loading }))
  .on(setBlocksEvent, (store, blocks) => ({ ...store, blocks }))
  .on(addBlockEvent, (store, newBlock) => {
    const blocks = store.blocks.filter(
      (block) => newBlock.id !== block.id && (newBlock.ghostId == null || newBlock.ghostId !== block.ghostId)
    );

    if (
      newBlock.type === PortalBlockType.PROJECTION &&
      newBlock.block &&
      !blocks.some(({ id }) => id === newBlock.sourceBlockId)
    ) {
      blocks.push({ ...newBlock.block, external: true });
    }

    blocks.push(newBlock);
    return { ...store, blocks };
  })
  .on(deleteBlockEvent, (store, { id, ghostId }) => {
    if (ghostId == null) {
      return { ...store, blocks: store.blocks.filter((block) => block.id !== id) };
    }
    const blocks = store.blocks.map((block) => (block.id === id ? { ...block, ghost: true } : block));
    return { ...store, blocks };
  })
  .on(updateBlockEvent, (store, block) => ({ ...store, blocks: updateBlockInArray(store.blocks, block) }))
  .on(updateBlockSizeEvent, (store, block) => ({ ...store, blocks: updateBlockInArray(store.blocks, block) }))
  .on(updateBlockVisibilityEvent, (store, block) => ({ ...store, blocks: updateBlockInArray(store.blocks, block) }))
  .on(updateBlockPlaceEvent, (store, block) => ({ ...store, blocks: updateBlockInArray(store.blocks, block) }))
  .on(updateBlockActionsEvent, (store, block) => ({ ...store, blocks: updateBlockInArray(store.blocks, block) }))
  .on(updateBlockIndexEvent, (store, { id, index, place }) => {
    const blocksByPlace = store.blocks.filter((block) => block.place === place && block.external !== true);
    const oldOrder = blocksByPlace.sort((a, b) => a.index - b.index).map((block) => block.id);
    if (!oldOrder.includes(id)) {
      return store;
    }
    const { movedItems, minIndex } = arrayMove(oldOrder, oldOrder.indexOf(id), index);
    const blocks = store.blocks.map((block) => {
      const index = movedItems.indexOf(block.id);
      if (index === -1) {
        return block;
      }
      return { ...block, index: minIndex + index };
    });
    return { ...store, blocks };
  })
  .on(pushBlockDataEvent, (store, payload) => {
    const block = store.blocks.find((block) => block.id === payload.id);
    if (block == null) {
      return store;
    }
    const data = [...block.data];
    for (const item of payload.data) {
      const index = data.findIndex(({ type }) => type === item.type);
      if (index === -1) {
        data.push(item);
        continue;
      }
      if (item.replaceData) {
        data[index] = item;
      }
      data[index].dataSet = [...data[index].dataSet, ...item.dataSet];
    }
    return { ...store, blocks: updateBlockInArray(store.blocks, { ...block, data }) };
  });

export const $externalBlocks = $blocksStore.map((store) => store.blocks.filter(({ external }) => external === true));
export const $pageBlocks = $blocksStore.map((store) => store.blocks.filter(({ external }) => external !== true));
export const $blocksLoading = $blocksStore.map((store) => store.loading);

export const $blocksState = $pageBlocks.map((blocks) => blocksToState(blocks));

export const $blocksByPlace = $blocksState.map((state) =>
  blockPlaces.reduce((acc, place) => {
    acc[place] = state[place].ids.map((id) => state[place].entities[id]);
    return acc;
  }, {} as BlocksByPlace)
);
export const $blocksOrderByPlace = $blocksState.map((state) =>
  blockPlaces.reduce((acc, place) => {
    acc[place] = state[place].ids;
    return acc;
  }, {} as BlocksOrderByPlace)
);
export const $hasNoteBlocksByPlace = $blocksState.map((state) =>
  blockPlaces.reduce((acc, place) => {
    acc[place] = state[place].ids.some((id) =>
      [PortalBlockType.NOTE, PortalBlockType.CONTENT].includes(state[place].entities[id].type)
    );
    return acc;
  }, {} as HasNoteBlocksByPlace)
);

export const $blockTemplatesType = $pageBlocks.map((blocks) => {
  return blocks.reduce((acc, item) => {
    if (item.type === PortalBlockType.FLEXIBLE) {
      acc.push(item.template.type);
    }
    return acc;
  }, [] as FlexibleBlockTemplateType[]);
});

export const $contentBlock = $blocksState.map(
  (state) => stateToBlocks(state).find((block) => block.type === PortalBlockType.CONTENT) as ContentBlock | undefined
);
